import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { trackPhoneConversion } from '../../utils/analytics';

const GRADIENT = 'linear-gradient(135deg, #14b8a6 0%, #10b981 100%)';
const TEXT_COLOR = '#1A1A1A';

export type ActivePage = 'faq' | 'about' | 'booking' | 'website-impact' | 'hotels' | null;

interface SiteNavProps {
  activePage?: ActivePage;
  navId: string;
  onRefsClick?: () => void;
}

export default function SiteNav({ activePage, navId, onRefsClick }: SiteNavProps) {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hideNav, setHideNav] = useState(false);
  const lastScrollYRef = useRef(0);

  const gradientTextStyle: React.CSSProperties = {
    background: GRADIENT,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  };

  const handleRefsClick = () => {
    setIsMobileMenuOpen(false);
    if (onRefsClick) {
      onRefsClick();
      return;
    }
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('transformation');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        setTimeout(() => window.dispatchEvent(new CustomEvent('openReferenzen')), 600);
      }
    }, 150);
  };

  // Scroll direction detection
  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const scrollingDown = currentY > lastScrollYRef.current;

      if (scrollingDown && currentY > 60) {
        setHideNav(true);
        setIsMobileMenuOpen(false);
      } else if (!scrollingDown) {
        setHideNav(false);
      }

      lastScrollYRef.current = currentY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setIsMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navLinks: { label: string; to?: string; page: ActivePage | null; isButton?: boolean }[] = [
    { label: 'Referenzen', page: null, isButton: true },
    { label: 'Hotels', to: '/hotels', page: 'hotels' },
    { label: 'Gute Gründe', to: '/website-wirkung', page: 'website-impact' },
    { label: 'Über Seitenschmied', to: '/ueber-uns', page: 'about' },
    { label: 'FAQ', to: '/faq', page: 'faq' },
  ];

  return (
    <nav
      id={navId}
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        backgroundColor: (hideNav && !isMobileMenuOpen) ? 'transparent' : 'rgba(255,255,255,0.95)',
        backdropFilter: (hideNav && !isMobileMenuOpen) ? 'none' : 'blur(12px)',
        boxShadow: (hideNav && !isMobileMenuOpen) ? 'none' : '0 2px 15px rgba(0,0,0,0.05)',
        transition: 'background-color 0.35s ease, backdrop-filter 0.35s ease, box-shadow 0.35s ease',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 sm:py-4">
        <div className="flex items-center justify-between">

          {/* Logo + Badge */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Link
              to="/"
              className="cursor-pointer flex-shrink-0"
              aria-label="Zurück zur Startseite"
            >
              <img
                src="https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/06f7ee287cfacc7a314e7ca2f415ab12.png"
                alt="seitenschmied"
                className="h-6 sm:h-7 w-auto"
              />
            </Link>
            {/* Badge — hidden when nav is hidden */}
            <span
              className="hidden sm:block w-px h-4 bg-gray-300 flex-shrink-0 transition-opacity duration-300"
              style={{ opacity: hideNav ? 0 : 1 }}
            />
            <span
              className="hidden sm:inline-flex items-center gap-1.5 text-gray-400 text-[10px] tracking-widest uppercase font-medium whitespace-nowrap transition-opacity duration-300"
              style={{ opacity: hideNav ? 0 : 1 }}
            >
              Websites made with <span className="text-amber-400">🧠</span>
            </span>
          </div>

          {/* Desktop Nav links + Call button — grouped right */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {/* Links — fades out when hidden */}
            <div
              className="flex items-center gap-6 lg:gap-8 transition-opacity duration-300"
              style={{ opacity: hideNav ? 0 : 1, pointerEvents: hideNav ? 'none' : 'auto' }}
            >
              {navLinks.map((link) => {
                const isActive = link.page === activePage;
                if (link.isButton) {
                  return (
                    <button
                      key={link.label}
                      onClick={handleRefsClick}
                      className="font-medium text-sm hover:opacity-70 transition-all duration-300 whitespace-nowrap cursor-pointer"
                      style={isActive ? gradientTextStyle : { color: TEXT_COLOR }}
                    >
                      {link.label}
                    </button>
                  );
                }
                return (
                  <Link
                    key={link.label}
                    to={link.to!}
                    className="font-medium text-sm hover:opacity-70 transition-all duration-300 whitespace-nowrap"
                    style={isActive ? gradientTextStyle : { color: TEXT_COLOR }}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>

            {/* Call button — always visible */}
            <a href="tel:+491607676763" onClick={(e) => { e.preventDefault(); trackPhoneConversion('tel:+491607676763'); }}>
              <button
                className="flex items-center gap-2 px-5 lg:px-6 py-2.5 font-semibold text-sm rounded-xl transition-all duration-300 hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer text-white"
                style={{ background: GRADIENT }}
              >
                <i className="ri-phone-line text-sm"></i>
                Jetzt anrufen
              </button>
            </a>
          </div>

          {/* Mobile Burger — gradient icon, always visible */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer flex-shrink-0"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
          >
            <i
              className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}
              style={{
                background: GRADIENT,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            ></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-3 pb-4">
            <div className="flex flex-col pt-3">
              {navLinks.map((link) => {
                const isActive = link.page === activePage;
                if (link.isButton) {
                  return (
                    <button
                      key={link.label}
                      onClick={handleRefsClick}
                      className="text-left font-medium text-sm hover:opacity-70 py-3 px-2 cursor-pointer rounded-lg"
                      style={isActive ? gradientTextStyle : { color: TEXT_COLOR }}
                    >
                      {link.label}
                    </button>
                  );
                }
                return (
                  <Link
                    key={link.label}
                    to={link.to!}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-left font-medium text-sm hover:opacity-70 py-3 px-2 block rounded-lg"
                    style={isActive ? gradientTextStyle : { color: TEXT_COLOR }}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <div className="pt-3">
                <a href="tel:+491607676763" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); trackPhoneConversion('tel:+491607676763'); }}>
                  <button
                    className="w-full flex items-center justify-center gap-2 px-6 py-3.5 font-semibold text-sm rounded-xl cursor-pointer text-white"
                    style={{ background: GRADIENT }}
                  >
                    <i className="ri-phone-line text-sm"></i>
                    Jetzt anrufen
                  </button>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}